import { z } from 'zod'

import { msTimestampSchema } from './common.ts'

// RAL-162/RAL-163: the not-found and something-broke pages. A reason code (404) or a failure
// class (500-ish) picks a fixed layout and fixed copy — never assembled from fragments at
// runtime (RAL-162 rule 6) — so both tables below are the single source every page reads from.

// Every case a 404 in this product can mean. No code falls through to a default layout —
// an unresolved code is a bug (log it, render `statement`, count it) rather than a silent guess.
export const NotFoundReasonEnum = z.enum([
  'clip_deleted',
  'post_removed',
  'campaign_ended',
  'wrong_context',
  'wrong_account',
  'access_revoked',
  'never_existed',
  'unknown_route'
])
export type NotFoundReason = z.infer<typeof NotFoundReasonEnum>

export const NotFoundLayoutEnum = z.enum(['ledger', 'switchboard', 'statement', 'refusal'])
export type NotFoundLayout = z.infer<typeof NotFoundLayoutEnum>

export const NOT_FOUND_LAYOUT_BY_REASON: Record<NotFoundReason, NotFoundLayout> = {
  clip_deleted: NotFoundLayoutEnum.enum.ledger,
  post_removed: NotFoundLayoutEnum.enum.ledger,
  campaign_ended: NotFoundLayoutEnum.enum.switchboard,
  wrong_context: NotFoundLayoutEnum.enum.switchboard,
  wrong_account: NotFoundLayoutEnum.enum.switchboard,
  access_revoked: NotFoundLayoutEnum.enum.refusal,
  never_existed: NotFoundLayoutEnum.enum.statement,
  unknown_route: NotFoundLayoutEnum.enum.statement
}

// One written headline per reason (RAL-162's "one page, eight reasons" table). The object's
// own details fill in around this sentence; the sentence itself is never composed at runtime.
export const NOT_FOUND_HEADLINE_BY_REASON: Record<NotFoundReason, string> = {
  clip_deleted: 'This clip was deleted',
  post_removed: 'This post is gone from TikTok',
  campaign_ended: 'That campaign ended — everything in it is still readable',
  wrong_context: 'That proposal is in a different campaign',
  wrong_account: 'That post belongs to another account in this workspace',
  access_revoked: 'An admin removed your access to this workspace',
  never_existed: 'There’s nothing at this address',
  unknown_route: 'There’s nothing at this address'
}

// Ledger layout's "what survived it" block: the record that outlives the deleted object.
// Required for the ledger layout to render at all — if this can't be loaded, NotFoundPage
// degrades to `statement` rather than showing a survivors block with blanks (RAL-163).
export const LedgerSubjectSchema = z.object({
  layout: z.literal(NotFoundLayoutEnum.enum.ledger),
  path: z.string(),
  objectLabel: z.string().min(1),
  deletedAt: msTimestampSchema,
  publishedRecord: z
    .object({ postCount: z.number().nonnegative(), publishedAt: msTimestampSchema, views: z.number().nonnegative() })
    .nullable(),
  hasActivityRows: z.boolean(),
  lostDetail: z.string()
})
export type LedgerSubject = z.infer<typeof LedgerSubjectSchema>

// Switchboard layout's switch row. `target.reachable` must be verified server-side before this
// is ever handed to the page — an unreachable target is `access_revoked`, not a switch that
// would fail (RAL-163). NotFoundPage only ever renders the row when `reachable` is true.
export const SwitchboardSubjectSchema = z.object({
  layout: z.literal(NotFoundLayoutEnum.enum.switchboard),
  path: z.string(),
  currentCampaignName: z.string(),
  target: z.object({
    name: z.string(),
    handle: z.string(),
    startAt: msTimestampSchema,
    endAt: msTimestampSchema.nullable(),
    ended: z.boolean(),
    reachable: z.boolean(),
    // Where "Take me there" actually navigates — that campaign's queue (RAL-163). Required
    // alongside `reachable` so the row is never rendered clickable with nowhere real to go.
    queueHref: z.string().min(1)
  })
})
export type SwitchboardSubject = z.infer<typeof SwitchboardSubjectSchema>

export const NotFoundSubjectSchema = z.discriminatedUnion('layout', [LedgerSubjectSchema, SwitchboardSubjectSchema])
export type NotFoundSubject = z.infer<typeof NotFoundSubjectSchema>

// -- Something broke -------------------------------------------------------

// RAL-162's "one page, seven failures" table, taken verbatim from the approved design
// (`Rally Error Pages.dc.html`'s embedded ERRORS matrix). `publishingSummary` is the one place
// a class asserts publishing state — ErrorPage only shows it once a publisher-status signal
// independent of the failure confirms it; otherwise it renders "We can't tell from here"
// instead (RAL-163's unknown-status requirement — silence beats a comforting guess).
export const FailureClassEnum = z.enum([
  'server',
  'agent_run',
  'publisher',
  'tiktok_down',
  'tiktok_refused',
  'rate_limited',
  'auth'
])
export type FailureClass = z.infer<typeof FailureClassEnum>

// The standalone frame (board 1e) is pre-auth only (RAL-163) — ErrorPage's props type enforces
// that at compile time by pairing `variant: 'standalone'` with `failureClass: 'auth'` literally,
// so this enum exists for the comparisons in error-page.tsx, not to widen what's allowed.
export const ErrorVariantEnum = z.enum(['shell', 'standalone'])
export type ErrorVariant = z.infer<typeof ErrorVariantEnum>

export const BlameEnum = z.enum(['rally', 'tiktok'])
export type Blame = z.infer<typeof BlameEnum>

export const PublishingToneEnum = z.enum(['ok', 'fail', 'waiting'])
export type PublishingTone = z.infer<typeof PublishingToneEnum>

export interface FailureClassCopy {
  headline: string
  publishingSummary: string
  publishingTone: PublishingTone
  blame: Blame
  // Rule 5: a retry that could itself trigger a publish attempt doesn't get a retry button —
  // it gets a route out instead. Only `publisher` (Rally "stopped trying") is unsafe to retry.
  retrySafe: boolean
}

export const FAILURE_CLASS_COPY: Record<FailureClass, FailureClassCopy> = {
  server: {
    headline: 'Rally can’t load this page',
    publishingSummary: 'unaffected',
    publishingTone: PublishingToneEnum.enum.ok,
    blame: BlameEnum.enum.rally,
    retrySafe: true
  },
  agent_run: {
    headline: 'Rally’s last run didn’t finish',
    publishingSummary: 'unaffected — nothing new scored',
    publishingTone: PublishingToneEnum.enum.ok,
    blame: BlameEnum.enum.rally,
    retrySafe: true
  },
  publisher: {
    headline: 'Rally couldn’t publish and stopped trying',
    publishingSummary: 'stopped — reverted to manual',
    publishingTone: PublishingToneEnum.enum.fail,
    blame: BlameEnum.enum.rally,
    retrySafe: false
  },
  tiktok_down: {
    headline: 'TikTok isn’t answering',
    publishingSummary: 'paused — will resume on its own',
    publishingTone: PublishingToneEnum.enum.waiting,
    blame: BlameEnum.enum.tiktok,
    retrySafe: true
  },
  tiktok_refused: {
    headline: 'TikTok refused the upload',
    publishingSummary: 'stopped for this post only',
    publishingTone: PublishingToneEnum.enum.waiting,
    blame: BlameEnum.enum.tiktok,
    retrySafe: true
  },
  rate_limited: {
    headline: 'Rally is waiting out a TikTok limit',
    publishingSummary: 'delayed — slots shift, nothing cancelled',
    publishingTone: PublishingToneEnum.enum.waiting,
    blame: BlameEnum.enum.tiktok,
    retrySafe: true
  },
  auth: {
    headline: 'Rally is having a bad minute',
    publishingSummary: 'unaffected — campaigns keep running',
    publishingTone: PublishingToneEnum.enum.ok,
    blame: BlameEnum.enum.rally,
    retrySafe: true
  }
}

// RAL-163's "publisher-status dependency": no source independent of the failing path exists
// yet (tracked as follow-up in Backend & Infrastructure). Every call site in this change passes
// `unknown` — ErrorPage renders the honest "can't tell" state rather than trusting
// FAILURE_CLASS_COPY's publishingSummary against unconfirmed live status.
export const PublisherStatusStateEnum = z.enum(['unaffected', 'stopped', 'unknown'])
export type PublisherStatusState = z.infer<typeof PublisherStatusStateEnum>

// `columns`, when present, is the rich three-column detail (RAL-162 board 1d) sourced from a
// real independent signal — specific counts and times, not the class's generic summary. Omitted
// whenever the caller only has the coarse state above; ErrorPage degrades to a single honest
// panel rather than filling columns with unverified specifics.
export const publisherStatusSchema = z.object({
  state: PublisherStatusStateEnum,
  asOf: msTimestampSchema.optional(),
  columns: z.object({ stillRunning: z.string(), stopped: z.string(), untouched: z.string() }).optional()
})
export type PublisherStatus = z.infer<typeof publisherStatusSchema>
